import { createContext, ReactNode, useMemo } from 'react';

import './TabbedPane.scss';

export type TabbedPaneProps<TabId extends string = string> = {
    children: ReactNode;
    activeTabId: TabId;
    renderTabContent: (tabId: TabId) => ReactNode;
    tabChanged?: (tabId: TabId) => void;
};

type TabbedPaneContext<TabId extends string = string> = {
    activeTabId: TabId;
    setTabId: (tabId: TabId) => void;
};

// eslint-disable-next-line
export const TabbedPaneContext = createContext<TabbedPaneContext<any>>({
    activeTabId: '',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setTabId: (_tabId: string): void => {
        throw new Error('Context provider missing');
    },
});

export const TabbedPane = <TabId extends string = string>(props: TabbedPaneProps<TabId>) => {
    const { children, activeTabId, renderTabContent, tabChanged } = props;

    const contextValue = useMemo(
        () => ({
            activeTabId,
            setTabId: (tab: TabId): void => {
                tabChanged?.(tab);
            },
        }),
        [activeTabId, tabChanged]
    );

    return (
        <TabbedPaneContext.Provider value={contextValue}>
            <div className="tabbed-pane">
                {children}
                <div className="tabbed-pane__content">{renderTabContent(activeTabId)}</div>
            </div>
        </TabbedPaneContext.Provider>
    );
};
